import Decimal from 'break_infinity.js'
import { Result, ok, err } from 'neverthrow'
import {
  GameState,
  SaveData,
  BuildingId,
  UpgradeId,
  createInitialGameState,
} from '../types'
import { BUILDING_ORDER } from '../data/buildings'

const SAVE_KEY = 'aiTokenClicker_saveData'
const CURRENT_SAVE_VERSION = 1

export const serializeState = (state: GameState): SaveData => {
  const buildings: Array<{ readonly id: BuildingId; readonly count: string }> = []

  for (const buildingId of BUILDING_ORDER) {
    const count = state.buildings.get(buildingId)
    if (count !== undefined && count.gt(0)) {
      buildings.push({ id: buildingId, count: count.toString() })
    }
  }

  const upgrades: UpgradeId[] = Array.from(state.upgrades) as UpgradeId[]

  return {
    version: CURRENT_SAVE_VERSION,
    tokens: state.tokens.toString(),
    totalClicks: state.totalClicks.toString(),
    buildings,
    upgrades,
  }
}

const isValidBuildingId = (id: string): id is BuildingId => {
  return BUILDING_ORDER.includes(id as BuildingId)
}

const isValidUpgradeId = (id: string): id is UpgradeId => {
  const validUpgradeIds: ReadonlyArray<UpgradeId> = [
    'mechanicalKeyboard',
    'dualMonitors',
    'ultrawideUpgrade',
    'standingDesk',
    'rgbEverything',
    'carpalTunnelBrace',
    'betterPrompts',
    'longerContext',
    'customInstructions',
    'multiModelEnsemble',
    'copilotCartel',
    'upgradeToSonnet4',
    'upgradeToOpus5',
    'autonomousLoops',
    'devContainers',
    'specDrivenDevelopment',
    'localKnowledge',
    'mSeriesChips',
    'betterThermals',
    'laptopStacking',
    'stolenWifi',
    'distributedTraining',
    'overclock',
    'waterCooling',
    'cudaOptimization',
    'usedRax3090',
    'modelDistillation',
    'miningRigConversion',
    'spotInstances',
    'reservedCapacity',
    'multiRegion',
    'kubernetes',
    'cloudLocalSync',
    'h100s',
    'infiniband',
    'liquidCooling',
    'onCallEngineer',
    'rackDensity',
    'coloDeal',
    'nuclearPowerContract',
    'riverCooling',
    'customAsics',
    'acquiredStartupHardware',
    'centralizedTraining',
    'verticalIntegration',
    'governmentContract',
    'submarineCables',
    'orbitalDatacenters',
    'fusionReactor',
    'globalNetworkEffect',
    'agiEmergentBehavior',
    'muscleMemory',
    'macroKeys',
    'autoclickerTolerance',
    'neuralLink',
  ]
  return validUpgradeIds.includes(id as UpgradeId)
}

export const deserializeState = (data: unknown): Result<GameState, string> => {
  if (typeof data !== 'object' || data === null) {
    return err('Invalid save data: not an object')
  }

  const saveData = data as Record<string, unknown>

  if (typeof saveData.version !== 'number') {
    return err('Invalid save data: missing version field')
  }

  if (saveData.version !== CURRENT_SAVE_VERSION) {
    return err(`Save version mismatch: expected ${CURRENT_SAVE_VERSION}, got ${saveData.version}`)
  }

  if (typeof saveData.tokens !== 'string') {
    return err('Invalid save data: missing or invalid tokens field')
  }

  if (typeof saveData.totalClicks !== 'string') {
    return err('Invalid save data: missing or invalid totalClicks field')
  }

  if (!Array.isArray(saveData.buildings)) {
    return err('Invalid save data: missing or invalid buildings field')
  }

  if (!Array.isArray(saveData.upgrades)) {
    return err('Invalid save data: missing or invalid upgrades field')
  }

  const tokensDecimal = new Decimal(saveData.tokens)
  const totalClicksDecimal = new Decimal(saveData.totalClicks)

  const buildingsMap = new Map<BuildingId, Decimal>()
  for (const building of saveData.buildings) {
    if (
      typeof building !== 'object' ||
      building === null ||
      typeof (building as Record<string, unknown>).id !== 'string' ||
      typeof (building as Record<string, unknown>).count !== 'string'
    ) {
      return err('Invalid save data: invalid building entry')
    }

    const buildingEntry = building as { id: string; count: string }
    if (!isValidBuildingId(buildingEntry.id)) {
      return err(`Invalid save data: unknown building id ${buildingEntry.id}`)
    }

    buildingsMap.set(buildingEntry.id, new Decimal(buildingEntry.count))
  }

  const upgradesSet = new Set<UpgradeId>()
  for (const upgradeId of saveData.upgrades) {
    if (typeof upgradeId !== 'string') {
      return err('Invalid save data: invalid upgrade entry')
    }
    if (!isValidUpgradeId(upgradeId)) {
      return err(`Invalid save data: unknown upgrade id ${upgradeId}`)
    }
    upgradesSet.add(upgradeId)
  }

  return ok({
    tokens: tokensDecimal,
    totalClicks: totalClicksDecimal,
    buildings: buildingsMap,
    upgrades: upgradesSet,
    lastTickMs: Date.now(),
  })
}

export const saveToLocalStorage = (state: GameState): Result<void, string> => {
  try {
    const saveData = serializeState(state)
    const jsonString = JSON.stringify(saveData)
    localStorage.setItem(SAVE_KEY, jsonString)
    return ok(undefined)
  } catch (error) {
    if (error instanceof Error) {
      return err(`Failed to save: ${error.message}`)
    }
    return err('Failed to save: unknown error')
  }
}

export const loadFromLocalStorage = (): Result<GameState | null, string> => {
  try {
    const jsonString = localStorage.getItem(SAVE_KEY)
    if (jsonString === null) {
      return ok(null)
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(jsonString)
    } catch {
      return err('Failed to parse save data: invalid JSON')
    }

    return deserializeState(parsed)
  } catch (error) {
    if (error instanceof Error) {
      return err(`Failed to load: ${error.message}`)
    }
    return err('Failed to load: unknown error')
  }
}

export const clearSave = (): Result<void, string> => {
  try {
    localStorage.removeItem(SAVE_KEY)
    return ok(undefined)
  } catch (error) {
    if (error instanceof Error) {
      return err(`Failed to clear save: ${error.message}`)
    }
    return err('Failed to clear save: unknown error')
  }
}

export const loadOrCreateState = (): GameState => {
  const loadResult = loadFromLocalStorage()
  if (loadResult.isOk()) {
    const savedState = loadResult.value
    if (savedState !== null) {
      return savedState
    }
  } else {
    console.warn('Failed to load save data, starting fresh:', loadResult.error)
  }
  return createInitialGameState()
}
